import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Gift, Save, Image as ImageIcon } from 'lucide-react';
import { createAward } from 'services/ranking.service';
import { RankingAward } from 'types/ranking';
import './styles.scss';

interface CreateAwardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAwardCreated: (award: RankingAward) => void;
}

const CreateAwardModal: React.FC<CreateAwardModalProps> = ({
  isOpen,
  onClose,
  onAwardCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Reset form quando modal fecha
  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', image: '', description: '' });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Nome do prêmio é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Impede propagação para form pai

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),
        image: formData.image.trim() || undefined,
        description: formData.description.trim() || undefined,
      };

      const newAward = await createAward(payload);

      onAwardCreated(newAward);
      
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Erro desconhecido ao criar prêmio';
      
      alert(`Erro ao criar prêmio: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpa erro do campo quando usuário digita
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  // Usa React Portal para renderizar o modal fora da hierarquia do DOM
  // Isso evita conflitos com o form do ranking
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-award-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="modal-icon">
              <Gift size={24} />
            </div>
            <h2 className="modal-title">Criar Novo Prêmio</h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {/* Nome do Prêmio */}
            <div className="form-group">
              <label className="form-label">
                NOME DO PRÊMIO *
              </label>
              <input
                type="text"
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="Ex: Viagem para Cancún"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={loading}
                autoFocus
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
              <p className="field-hint">
                Digite um nome atraente para o prêmio
              </p>
            </div>

            {/* URL da Imagem */}
            <div className="form-group">
              <label className="form-label">
                URL DA IMAGEM
              </label>
              <div className="input-with-icon">
                <ImageIcon size={18} className="input-icon" />
                <input
                  type="text"
                  className="form-input with-icon"
                  placeholder="https://exemplo.com/imagem.jpg"
                  value={formData.image}
                  onChange={(e) => handleInputChange('image', e.target.value)}
                  disabled={loading}
                />
              </div>
              <p className="field-hint">
                URL da imagem do prêmio (opcional)
              </p>
            </div>

            {/* Preview da Imagem */}
            {formData.image && (
              <div className="image-preview-container">
                <p className="preview-label">Preview da Imagem:</p>
                <div className="image-preview">
                  <img
                    src={formData.image}
                    alt="Preview do prêmio"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      const parent = (e.target as HTMLElement).parentElement;
                      if (parent) {
                        parent.innerHTML = '<div class="image-error">❌ URL inválida</div>';
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Descrição */}
            <div className="form-group">
              <label className="form-label">
                DESCRIÇÃO
              </label>
              <textarea
                className="form-textarea"
                placeholder="Descreva o prêmio e as condições para ganhá-lo..."
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                disabled={loading}
              />
              <p className="field-hint">
                Adicione detalhes sobre o prêmio (opcional)
              </p>
            </div>
          </div>

          {/* Footer com botões */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  Criando...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Criar Prêmio
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body // Renderiza no body, fora da hierarquia do formulário
  );
};

export default CreateAwardModal;
